/**
 * CurrencyInput Component
 * Currency input with validation and formatting
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Input, InputProps } from '../ui/Input';
import { cn } from '../../utils/cn';

export interface CurrencyInputProps extends Omit<InputProps, 'value' | 'onChange' | 'type'> {
  value?: number;
  onChange?: (value: number | null) => void;
  currency?: string;
  locale?: string;
  allowNegative?: boolean;
  maxValue?: number;
  minValue?: number;
}

export function CurrencyInput({
  value,
  onChange,
  currency = 'USD',
  locale = 'en-US',
  allowNegative = false,
  maxValue,
  minValue = 0,
  className,
  error,
  ...props
}: CurrencyInputProps) {
  const [displayValue, setDisplayValue] = useState('');
  const [focused, setFocused] = useState(false);

  // Format number as currency (stable identity)
  const formatCurrency = useCallback((amount: number | null) => {
    if (amount === null || amount === undefined) return '';
    
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (error) {
      // Fallback formatting
      return `$${amount.toFixed(2)}`;
    }
  }, [locale, currency]);

  // Parse currency string to number
  const parseCurrency = (str: string): number | null => {
    if (!str) return null;
    
    // Remove currency symbols and formatting
    const cleanStr = str
      .replace(/[^\d.-]/g, '') // Keep only digits, dots, and minus
      .replace(/\.(?=.*\.)/g, ''); // Remove all but the last decimal point
    
    const parsed = parseFloat(cleanStr);
    return isNaN(parsed) ? null : parsed;
  };

  // Update display value when value prop changes
  useEffect(() => {
    if (!focused) {
      setDisplayValue(value ? formatCurrency(value) : '');
    }
  }, [value, focused, currency, locale, formatCurrency]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    // Show raw number when focused for easier editing
    setDisplayValue(value ? value.toString() : '');
    props.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    const numericValue = parseCurrency(displayValue);
    
    // Validate and format
    let validatedValue = numericValue;
    
    if (validatedValue !== null) {
      // Apply min/max constraints
      if (minValue !== undefined && validatedValue < minValue) {
        validatedValue = minValue;
      }
      if (maxValue !== undefined && validatedValue > maxValue) {
        validatedValue = maxValue;
      }
      
      // Check negative values
      if (!allowNegative && validatedValue < 0) {
        validatedValue = 0;
      }
    }
    
    // Update display with formatted currency
    setDisplayValue(validatedValue ? formatCurrency(validatedValue) : '');
    
    // Notify parent of change
    if (onChange && validatedValue !== value) {
      onChange(validatedValue);
    }
    
    props.onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setDisplayValue(inputValue);
    
    // If focused, parse and validate in real-time
    if (focused) {
      // Parse to keep parent value in sync if needed. Validation enforced on blur.
      void parseCurrency(inputValue);
    }
  };

  const getCurrencySymbol = () => {
    try {
      const formatted = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(0);
      
      return formatted.replace(/\d/g, '').trim();
    } catch (error) {
      return '$'; // Fallback
    }
  };

  // Validation errors
  let validationError = error;
  if (!validationError && value !== null && value !== undefined) {
    if (!allowNegative && value < 0) {
      validationError = 'Negative values are not allowed';
    } else if (minValue !== undefined && value < minValue) {
      validationError = `Minimum value is ${formatCurrency(minValue)}`;
    } else if (maxValue !== undefined && value > maxValue) {
      validationError = `Maximum value is ${formatCurrency(maxValue)}`;
    }
  }

  return (
    <Input
      {...props}
      type="text"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      error={validationError}
      leftIcon={
        <span className="text-gray-500 font-medium">
          {getCurrencySymbol()}
        </span>
      }
      className={cn('text-right', className)}
      placeholder={focused ? '0.00' : formatCurrency(0)}
    />
  );
}
